import React, { useState } from 'react';

import { client } from '../../client/client.gen';
import type { FeatureFlags } from '../../client';
import {
  useGenerateData,
  useResetSimulation,
  useServerFeatureFlags,
  useSetPauseAt,
  useSimulationStatus,
  useStartSimulation,
  usePauseSimulation,
  useResumeSimulation,
  useEndSimulation,
  useDocumentTitle,
  useSimulationId,
} from '../../hooks';
import type { SimulationStatus } from '../../hooks';
import { formatSimulationTime, formatSimulationTimeDetailed } from '../../utils';

import { Button, Spinner, Toggle } from 'design-system/react';
import { HoldToClickButton, NumberInput } from '../molecules';
import { SimulationStateBadge } from '../atoms';
import { Page } from '../organisms';

export function OperatorPage() {
  const simId = useSimulationId();
  const resetSimulation = useResetSimulation();
  const generateData = useGenerateData();
  const featureFlags = useServerFeatureFlags();
  const simulationStatus = useSimulationStatus();
  const startSimulation = useStartSimulation();
  const pauseSimulation = usePauseSimulation();
  const resumeSimulation = useResumeSimulation();
  const endSimulation = useEndSimulation();
  const setPauseAt = useSetPauseAt();
  const [pauseAtMinutes, setPauseAtMinutes] = useState<number>(5);
  const [secondsPerDay, setSecondsPerDay] = useState<number>(10);
  const [nTeams, setNTeams] = useState<number>(7);
  const [nFeatures, setNFeatures] = useState<number>(100);
  const [maxEffort, setMaxEffort] = useState<number>(100);
  const [minTasksPerFeature, setMinTasksPerFeature] = useState<number>(2);
  const [maxTasksPerFeature, setMaxTasksPerFeature] = useState<number>(5);
  const [seed, setSeed] = useState<string>('');

  useDocumentTitle('Operator Dashboard');

  const downloadFlowData = async () => {
    const baseURL = client.getConfig().baseURL ?? '';
    const url = `${baseURL}/api/simulations/${simId}/analytics?seconds_per_day=${secondsPerDay}`;
    window.open(url, '_blank');
  };

  const handleFeatureFlagChange =
    <K extends keyof FeatureFlags>(featureKey: K) =>
    async (newValue: boolean) => {
      await featureFlags.mutateAsync({
        requestBody: {
          [featureKey]: newValue,
        },
      });
    };

  const featureFlagValue = <K extends keyof FeatureFlags>(featureKey: K): boolean | undefined => {
    return featureFlags.isFetching ? undefined : featureFlags.data?.[featureKey];
  };

  return (
    <Page title={null}>
      {/* Simulation Card — name, status, controls */}
      <div className="w-full bg-bg-elevated border border-border-muted rounded-lg p-4 mb-4">
        {simulationStatus.state !== 'loading' && (
          <h2 className="text-lg font-bold text-text-primary mb-3">{simulationStatus.label}</h2>
        )}
        <SimulationStateIndicator simulationStatus={simulationStatus} />

        <SimulationControlButtons
          simulationStatus={simulationStatus}
          onStart={() => startSimulation.mutate()}
          onPause={() => pauseSimulation.mutate()}
          onResume={() => resumeSimulation.mutate()}
          onEnd={() => endSimulation.mutate()}
          startLoading={startSimulation.isPending}
          pauseLoading={pauseSimulation.isPending}
          resumeLoading={resumeSimulation.isPending}
          endLoading={endSimulation.isPending}
        />

        {/* Auto-Pause Control — available in any state except finished/loading */}
        {simulationStatus.state !== 'loading' && simulationStatus.state !== 'finished' && (
          <>
            <RowItem title="Auto-pause at">
              <NumberInput
                value={pauseAtMinutes}
                onChange={(v) => setPauseAtMinutes(parseInt(v) || 1)}
                min={1}
                max={120}
                width="90px"
              />
              <span className="self-center text-sm">min</span>
              <Button
                variant="outline"
                onClick={() => setPauseAt.mutate(pauseAtMinutes * 60)}
                disabled={setPauseAt.isPending}
              >
                Set
              </Button>
              {simulationStatus.state !== 'not-started' && (
                <Button
                  variant="outline"
                  onClick={() => {
                    const target = simulationStatus.simulationTimeS + pauseAtMinutes * 60;
                    setPauseAt.mutate(target);
                  }}
                  disabled={setPauseAt.isPending}
                >
                  +{pauseAtMinutes}min
                </Button>
              )}
              <Button variant="outline" onClick={() => setPauseAt.mutate(null)} disabled={setPauseAt.isPending}>
                Clear
              </Button>
            </RowItem>
            {simulationStatus.pauseAtS != null && (
              <p className="text-xs text-text-muted ml-2 -mt-1 mb-1">
                Auto-pause target: {formatSimulationTime(simulationStatus.pauseAtS)}
              </p>
            )}
          </>
        )}

        <RowItem title="Reset simulation">
          <HoldToClickButton colorScheme="red" onComplete={() => resetSimulation.mutate()}>
            Reset
          </HoldToClickButton>
        </RowItem>
      </div>

      {/* Flow Metrics Card */}
      <div className="w-full bg-bg-elevated border border-border-muted rounded-lg p-4 mb-4">
        <RowItem title="Flow metrics">
          <span className="self-center text-sm">Seconds per day:</span>
          <NumberInput
            value={secondsPerDay}
            onChange={(valueString) => setSecondsPerDay(parseInt(valueString) || 1)}
            min={1}
            max={3600}
            width="110px"
          />
          <span className="self-center text-sm">seconds</span>
          <Button variant="primary" onClick={downloadFlowData}>
            Download
          </Button>
        </RowItem>
      </div>

      {/* Generate Data Card */}
      <div className="w-full bg-bg-elevated border border-border-muted rounded-lg p-4 mb-4">
        <p className="text-sm font-bold mb-2">Generate Data</p>

        <RowItem title="Teams">
          <NumberInput value={nTeams} onChange={(v) => setNTeams(parseInt(v) || 7)} min={2} max={20} width="90px" />
        </RowItem>

        <RowItem title="Features">
          <NumberInput
            value={nFeatures}
            onChange={(v) => setNFeatures(parseInt(v) || 100)}
            min={1}
            max={500}
            width="90px"
          />
        </RowItem>

        <RowItem title="Max effort">
          <NumberInput
            value={maxEffort}
            onChange={(v) => setMaxEffort(parseInt(v) || 100)}
            min={10}
            max={100}
            width="90px"
          />
        </RowItem>
        <p className="text-xs text-text-muted ml-2 -mt-1 mb-1">10 = all small tasks, 100 = full range</p>

        <RowItem title="Min tasks/feature">
          <NumberInput
            value={minTasksPerFeature}
            onChange={(v) => setMinTasksPerFeature(parseInt(v) || 2)}
            min={1}
            max={10}
            width="90px"
          />
        </RowItem>

        <RowItem title="Max tasks/feature">
          <NumberInput
            value={maxTasksPerFeature}
            onChange={(v) => setMaxTasksPerFeature(parseInt(v) || 5)}
            min={1}
            max={10}
            width="90px"
          />
        </RowItem>

        <RowItem title="Seed">
          <input
            type="text"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            placeholder="Random"
            className="w-[90px] px-2 py-1 text-sm border border-border-default rounded bg-bg-default text-text-primary placeholder:text-text-muted"
          />
        </RowItem>
        <p className="text-xs text-text-muted ml-2 -mt-1 mb-1">Set a number for reproducible data</p>

        <div className="flex justify-end mt-3">
          <HoldToClickButton
            colorScheme="red"
            onComplete={() =>
              generateData.mutate({ nTeams, nFeatures, maxEffort, minTasksPerFeature, maxTasksPerFeature, seed })
            }
          >
            Generate
          </HoldToClickButton>
        </div>
      </div>

      {/* Feature Flags Card */}
      <div className="w-full bg-bg-elevated border border-border-muted rounded-lg p-4 mb-4">
        <p className="text-sm font-bold mb-2">Feature flags</p>

        <p className="text-xs text-text-muted mt-2 mb-1">Iteration 2</p>
        <hr className="border-border-default mb-1" />
        <FeatureFlagSwitch
          name={'Show "Features" in menu'}
          value={featureFlagValue('features')}
          onChange={handleFeatureFlagChange('features')}
        />
        <FeatureFlagSwitch
          name={'Show "Feature" badge for tasks'}
          value={featureFlagValue('show_feature_badge')}
          onChange={handleFeatureFlagChange('show_feature_badge')}
        />
        <FeatureFlagSwitch
          name={'Show feature earnings'}
          value={featureFlagValue('show_feature_earnings')}
          onChange={handleFeatureFlagChange('show_feature_earnings')}
        />
        <FeatureFlagSwitch
          name={'Show feature size'}
          value={featureFlagValue('show_feature_size')}
          onChange={handleFeatureFlagChange('show_feature_size')}
        />
        <FeatureFlagSwitch
          name={"Show # of my team's tasks in feature"}
          value={featureFlagValue('show_number_of_team_tasks')}
          onChange={handleFeatureFlagChange('show_number_of_team_tasks')}
        />
        <FeatureFlagSwitch
          name={'Show weighted value (tasks × earnings)'}
          value={featureFlagValue('show_weighted_value')}
          onChange={handleFeatureFlagChange('show_weighted_value')}
        />

        <p className="text-xs text-text-muted mt-3 mb-1">Iteration 3</p>
        <hr className="border-border-default mb-1" />
        <FeatureFlagSwitch
          name={'Allow feature sorting'}
          value={featureFlagValue('allow_feature_sorting')}
          onChange={handleFeatureFlagChange('allow_feature_sorting')}
        />
        <FeatureFlagSwitch
          name={'Allow task sorting'}
          value={featureFlagValue('allow_task_sorting')}
          onChange={handleFeatureFlagChange('allow_task_sorting')}
        />
      </div>

      {/* Version Info */}
      <p className="text-xs text-text-muted text-center mt-4">{import.meta.env.VITE_APP_VERSION || 'dev'}</p>
    </Page>
  );
}

interface FeatureFlagSwitchProps {
  name: string;
  value?: boolean;
  onChange: (new_value: boolean) => Promise<void>;
}

const FeatureFlagSwitch = ({ name, value, onChange }: FeatureFlagSwitchProps) => {
  const [mutating, setMutating] = useState<boolean>(false);

  const handleChange = async () => {
    setMutating(true);
    await onChange(!value);
    setMutating(false);
  };

  const loading = value === undefined || mutating;

  // Toggle renders its label as visible text inside the switch's hit target,
  // so the row carries no separate title (it would print the name twice).
  return (
    <div className="flex items-center gap-2 w-full m-1 p-1 rounded transition-colors hover:bg-bg-subtle">
      <Toggle checked={value ?? false} onChange={handleChange} disabled={loading} label={name} />
      {loading && <Spinner size="sm" />}
    </div>
  );
};

interface Props {
  title: string;
  children: React.ReactNode;
}

interface SimulationControlButtonsProps {
  simulationStatus: SimulationStatus;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onEnd: () => void;
  startLoading: boolean;
  pauseLoading: boolean;
  resumeLoading: boolean;
  endLoading: boolean;
}

function SimulationControlButtons({
  simulationStatus,
  onStart,
  onPause,
  onResume,
  onEnd,
  startLoading,
  pauseLoading,
  resumeLoading,
  endLoading,
}: SimulationControlButtonsProps) {
  const { state } = simulationStatus;

  return (
    <div className="w-full mb-4">
      <p className="text-sm font-medium mb-3">Simulation Controls:</p>

      {/* Start Button */}
      {state === 'not-started' && (
        <RowItem title="Start simulation">
          <Button variant="primary" onClick={onStart} disabled={startLoading}>
            {startLoading ? 'Starting...' : 'Start'}
          </Button>
        </RowItem>
      )}

      {/* Pause/Resume Toggle */}
      {(state === 'running' || state === 'paused') && (
        <RowItem title={state === 'running' ? 'Pause simulation' : 'Resume simulation'}>
          {state === 'running' ? (
            <Button
              className="bg-state-paused hover:brightness-90 text-text-primary"
              onClick={onPause}
              disabled={pauseLoading}
            >
              {pauseLoading ? 'Pausing...' : 'Pause'}
            </Button>
          ) : (
            <Button className="bg-state-running hover:brightness-90" onClick={onResume} disabled={resumeLoading}>
              {resumeLoading ? 'Resuming...' : 'Resume'}
            </Button>
          )}
        </RowItem>
      )}

      {/* End Button */}
      {(state === 'running' || state === 'paused') && (
        <RowItem title={'End simulation'}>
          <HoldToClickButton colorScheme="red" onComplete={onEnd} isLoading={endLoading}>
            End
          </HoldToClickButton>
        </RowItem>
      )}

      {/* Finished State Display */}
      {state === 'finished' && (
        <RowItem title={'Simulation finished'}>
          <span className="text-state-finished font-medium">Simulation completed ✓</span>
        </RowItem>
      )}
    </div>
  );
}

function SimulationStateIndicator({ simulationStatus }: { simulationStatus: SimulationStatus }) {
  if (simulationStatus.state === 'loading') {
    return (
      <div className="w-full mb-3 p-2 rounded bg-bg-muted">
        <div className="flex justify-between items-start">
          <span className="text-sm font-medium shrink-0">Status:</span>
          <SimulationStateBadge state="loading" />
        </div>
      </div>
    );
  }

  const { simulationTimeS, iterationDurationS, remainingS } = simulationStatus;

  return (
    <div className="w-full mb-3 p-2 rounded bg-bg-muted">
      <div className="flex justify-between items-start">
        <span className="text-sm font-medium shrink-0">Status:</span>
        <SimulationStateBadge state={simulationStatus.state} />
      </div>

      <div className="flex flex-col gap-1 mt-2">
        {iterationDurationS != null && iterationDurationS > 0 && (
          <div className="flex justify-between w-full">
            <span className="text-xs text-text-muted">Iteration duration:</span>
            <span className="text-xs font-mono">{Math.round(iterationDurationS / 60)}min</span>
          </div>
        )}

        {simulationStatus.state !== 'not-started' && (
          <>
            <div className="flex justify-between w-full">
              <span className="text-xs text-text-muted">Total elapsed:</span>
              <span className="text-xs font-medium font-mono">{formatSimulationTime(simulationTimeS)}</span>
            </div>

            {(simulationStatus.state === 'running' || simulationStatus.state === 'paused') &&
              remainingS != null &&
              remainingS > 0 && (
                <div className="flex justify-between w-full">
                  <span className="text-xs text-feedback-warning">Until pause:</span>
                  <span className="text-xs font-mono text-feedback-warning font-medium">
                    {formatSimulationTime(remainingS)}
                  </span>
                </div>
              )}

            {simulationStatus.state === 'running' && remainingS != null && remainingS > 0 && (
              <div className="flex justify-between w-full">
                <span className="text-xs text-text-muted">Expected pause:</span>
                <span className="text-xs text-text-muted">
                  {new Date(Date.now() + remainingS * 1000).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })}
                </span>
              </div>
            )}
          </>
        )}

        {simulationStatus.state === 'paused' && (
          <div className="flex justify-between w-full">
            <span className="text-xs text-state-paused italic">
              ⏸️ Paused at {formatSimulationTimeDetailed(simulationTimeS)}
            </span>
          </div>
        )}

        {simulationStatus.state === 'finished' && (
          <div className="flex justify-between w-full">
            <span className="text-xs text-state-finished italic">
              ✅ Completed in {formatSimulationTimeDetailed(simulationTimeS)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function RowItem({ title, children }: Props) {
  return (
    <div className="w-full block">
      <div className="flex items-center justify-between gap-2 w-full m-1 p-1 rounded transition-colors hover:bg-bg-subtle">
        <span className="text-sm shrink-0">{title}</span>
        <div className="flex items-center justify-end gap-1 flex-wrap min-w-0">{children}</div>
      </div>
    </div>
  );
}
